import React from "react";
import AssetLoader from "@/components/AssetLoader/AssetLoader";
import { AssetTypes } from "@/model/AssetTypes";
import DebugDataComponent from "@/debug/DebugDataComponent";
import "./VacancyApplicantListEntry.scss";
import BFValueDisplay from "@/modules/abstract-ui/data/value-display/BFValueDisplay";
import i18n from "@/i18n";
import {
  APOffer,
  APOfferData,
} from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import useVacancyActivity from "../../../hooks/useVacancyActivity";
import {
  APOfferVacancyMeta,
  VacancyApplicant,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import VacancyApplicantEmploymentStatus from "../employment-status/VacancyApplicantEmploymentStatus";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import VacancyApplicantWorkflowStatus from "../vacancy-applicant-wf-status/VacancyApplicantWorkflowStatus";
import VacancyApplicantCreditReportStatus from "../credit-report/VacancyApplicantCreditReportStatus";
import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import classNames from "classnames";
import useVacancyApplicantDetails from "../../../hooks/useVacancyApplicantDetails";
import VacancyOfferUtils from "../../utils/VacancyOfferUtils";
import BFTabContainer from "@/modules/abstract-ui/data/tabs/BFTabContainer";
import moment from "moment";

type Props = {
  offer: APOffer;
  activity: APActivity;
  constants: ActivityApplicationConstants;
  editable?: boolean;
  hideBorder?: boolean;
  hideWfStatus?: boolean;
};

const VacancyApplicantListEntry = (props: Props) => {
  const {
    offer,
    activity,
    constants,
    editable = false,
    hideBorder = false,
  } = props;

  const offerData = offer?.data as APOfferData<APOfferVacancyMeta>;
  if (!offerData) {
    return (
      <div
        className={classNames("vacancy-applicant-list-entry", {
          border: !hideBorder,
        })}
      >
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.NoOfferData",
          "Kein Mietinteressent gefunden"
        )}
      </div>
    );
  }

  const applicationId = offerData.meta.applicationId;

  const renderApplication = (application: VacancyApplication) => {
    if (!application) {
      return (
        <div
          className={classNames("vacancy-applicant-list-entry", {
            border: !hideBorder,
          })}
        >
          -
        </div>
      );
    }
    const mainContractPerson =
      application.data.applicants?.find((c) => c.isMain) ||
      application.data.applicants?.[0];

    if (!mainContractPerson) {
      return (
        <div
          className={classNames("vacancy-applicant-list-entry", {
            border: !hideBorder,
          })}
        >
          {i18n.t(
            "apTemplate:Vacancy.Applicant.Label.NoMainContractPerson",
            "Kein Hauptvertragspartner gefunden"
          )}
        </div>
      );
    }

    return (
      <div
        className={classNames("vacancy-applicant-list-entry", {
          border: !hideBorder,
        })}
      >
        <DebugDataComponent data={offer} />
        <DebugDataComponent data={application} />
        <BFTabContainer
          defaultActiveKey={mainContractPerson.id}
          tabs={application.data.applicants
            ?.sort((a) => (a.isMain ? -1 : 1))
            .map((applicant) => {
              return {
                key: applicant.id,
                id: applicant.id,
                label: `${applicant.firstName} ${applicant.lastName}`,
                content: () => (
                  <ApplicantEntry
                    key={applicant.id}
                    application={application}
                    applicant={applicant}
                    activity={activity}
                    offer={offer}
                    constants={constants}
                    editable={editable}
                    hideWfStatus={props.hideWfStatus}
                  />
                ),
                activeTab: mainContractPerson.id,
              };
            })}
        />
      </div>
    );
  };

  return (
    <AssetLoader
      assetType={AssetTypes.ActivityRelated.VacancyApplication}
      id={applicationId}
      render={renderApplication}
    />
  );
};

export default VacancyApplicantListEntry;

type ApplicantEntryProps = {
  application: VacancyApplication;
  applicant: VacancyApplicant;
  activity: APActivity;
  offer: APOffer;
  constants: ActivityApplicationConstants;
  editable: boolean;
  hideWfStatus?: boolean;
  renderGuarantor?: boolean;
  onOpenGuarantorModal?: () => void;
};

const ApplicantEntry = (props: ApplicantEntryProps) => {
  const {
    activity,
    applicant,
    application,
    offer,
    constants,
    editable = false,
    hideWfStatus = false,
  } = props;

  const { getEmploymentStatusLabel } = useVacancyActivity();
  const { openApplicantDetailsModal } = useVacancyApplicantDetails();

  const employmentStatus = getEmploymentStatusLabel(
    applicant.employmentInfo?.status
  );

  return (
    <div>
      <div className="__h3 __flex">
        {i18n.t("apTemplate:Vacancy.Applicant.Label.MainTitle", "Interessent")}
        <BFButton
          size="xs"
          appearance="link"
          onClick={() => {
            openApplicantDetailsModal({
              activity,
              application,
              applicant,
              offer,
              constants,
              editable,
              onOpenGuarantorModal: () =>
                openApplicantDetailsModal({
                  activity,
                  application,
                  applicant,
                  offer,
                  constants,
                  editable,
                  renderGuarantor: true,
                }),
            });
          }}
        >
          {i18n.t(
            "apTemplate:Vacancy.Applicant.Label.OpenDetailed",
            "Details öffnen"
          )}
        </BFButton>
      </div>
      <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
        <BFValueDisplay
          label={i18n.t("apTemplate:Vacancy.Applicant.Label.FullName", "Name")}
          value={`${applicant.firstName} ${applicant.lastName}`}
        />
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.EmploymentState",
            "Beschäftigungsverhältnis"
          )}
          value={
            <>
              <VacancyApplicantEmploymentStatus
                application={application}
                applicant={applicant}
                activity={activity}
                constants={constants}
                onOpenGuarantorModal={() => {
                  openApplicantDetailsModal({
                    activity,
                    application,
                    applicant,
                    offer,
                    constants,
                    renderGuarantor: true,
                    editable,
                  });
                }}
              />

              {` ${employmentStatus || "-"}`}
            </>
          }
        />
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.Title",
            "Bonität"
          )}
          value={
            <>
              <VacancyApplicantCreditReportStatus
                applicantId={applicant.id}
                application={application}
                constants={constants}
                editable={editable}
              />
              {` ${
                VacancyOfferUtils.getCreditWorthinessLabel(
                  applicant.creditWorthiness
                ) ||
                i18n.t(
                  "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.ValueNotSet",
                  "Nicht erfasst"
                )
              }`}
            </>
          }
        />
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.MoveIn.Title",
            "Einzugstermin"
          )}
          value={
            <div>
              {moment(application.data.moveIn).format("DD.MM.YYYY") || "-"}
            </div>
          }
        />
      </div>
      {!hideWfStatus && (
        <VacancyApplicantWorkflowStatus
          application={application}
          applicant={applicant}
          activity={activity}
          offer={offer}
          constants={constants}
          hideActions={!editable}
        />
      )}
    </div>
  );
};
